import { useMutation, useQueryClient } from '@tanstack/react-query'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { CompanyProfileApiService } from 'src/services/companyProfileApi'
import { CompanyProfileData } from 'src/pages/Settings/CompanyProfile/types'

interface UseSaveCompanyProfileMutationProps {
  onError?: () => void
  onSuccess?: () => void
}

// PUT writes the whole profile — the page's first save. PATCH updates one
// section, which is what Edit does afterwards. Both refresh the cached read so
// the summary shows what the server actually stored, not what was typed.
export const useSaveCompanyProfileMutation = ({
  onError,
  onSuccess,
}: UseSaveCompanyProfileMutationProps = {}) => {
  const queryClient = useQueryClient()

  const settle = () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_COMPANY_PROFILE] })
    onSuccess?.()
  }

  const replace = useMutation({
    mutationFn: (payload: CompanyProfileData) =>
      CompanyProfileApiService.replace(payload),
    onError: () => onError?.(),
    onSuccess: settle,
  })

  const update = useMutation({
    mutationFn: (payload: Partial<CompanyProfileData>) =>
      CompanyProfileApiService.update(payload),
    onError: () => onError?.(),
    onSuccess: settle,
  })

  return {
    isSaving: replace.isPending || update.isPending,
    replace: replace.mutate,
    update: update.mutate,
  }
}
